import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { useTheme } from "../../contexts/Theme/ThemeContext";
import { obterMiniaturaVideoYouTube } from "../../services/Functions";
import { Produto as ProdutoType } from "../../types/Produto";
import ImgWithLoading from "../ImgWithLoading/ImgWithLoading";
import ProdutoWrapper from "./styles";

interface AjusteInfo {
  tipo: string;
  direcao: string;
  valor: number;
}

interface ProdutoProps {
  produto: ProdutoType & { ajuste?: AjusteInfo };
  children?: React.ReactNode;
  moeda?: string;
  onClick?: (e) => void;
  openImage?: boolean;
}

const formatAjusteLabel = (ajuste: AjusteInfo, moeda: string): string => {
  const sinal = ajuste.direcao === 'diminuir' ? '-' : '+';
  if (ajuste.tipo === 'porcentagem') {
    return `${sinal}${(ajuste.valor / 100).toFixed(0)}%`;
  } else if (ajuste.tipo === 'valor_fixo') {
    return `${sinal}${moeda}${(ajuste.valor / 100).toFixed(2).replace('.', ',')}`;
  } else if (ajuste.tipo === 'valor_final') {
    return `${moeda}${(ajuste.valor / 100).toFixed(2).replace('.', ',')}`;
  }
  return '';
};

const formatPrecoRange = (produto: ProdutoType, moeda: string): string | null => {
  const precoMin = produto.preco_min ? Number(produto.preco_min.replace(",", ".")) : null;
  const precoMax = produto.preco_max ? Number(produto.preco_max.replace(",", ".")) : null;

  if (precoMin === null && precoMax === null) return null;

  if ((precoMin === 0 || precoMin === null) && (precoMax === 0 || precoMax === null)) {
    return "A combinar";
  }

  // Preços iguais = mostrar único
  if (precoMin === precoMax) {
    return precoMax && precoMax > 0 ? `${moeda}${produto.preco_max}` : "A combinar";
  }

  // Faixa de preços
  return `${moeda}${produto.preco_min} - ${moeda}${produto.preco_max}`;
};

const Produto: React.FC<ProdutoProps> = ({ produto, moeda="R$",children, onClick, openImage }) => {
  const { theme } = useTheme();

  const [loaded, setLoaded] = useState(false);
  const [imgSrc, setImgSrc] = useState("");

  useEffect(() => {
    if (produto.imagens[0] && (produto.imagens[0].includes("youtube.com") || produto.imagens[0].includes("youtu.be"))) {
      setImgSrc(obterMiniaturaVideoYouTube(produto.imagens[0]));
    } else {
      setImgSrc(produto.imagens[0]);
    }
  }, [produto]);

  const MySwal = withReactContent(Swal);
  
  const handleImageClick = () => {
    if (!imgSrc || !openImage) return;
    MySwal.fire({
      title: produto.nome,
      html: `<img src=${imgSrc.replace("{size}", "900")} style="width: 100%; height: 100%; object-fit: contain; border-radius: 4rem"/>`,
    });
  };

  return (
    <ProdutoWrapper className="produto-wrapper" onClick={onClick} theme={theme}>
      <div className="produto">
        <div className="imagem">
          {produto.nome_tag_destaque && produto.tag_destaque_ativo === 1 && (
            <div className="tag-destaque">{produto.nome_tag_destaque}</div>
          )}
          {produto.ajuste && (
            <div className={`ajuste-label ${produto.ajuste.direcao}`}>
              {formatAjusteLabel(produto.ajuste, moeda)}
            </div>
          )}
          <ImgWithLoading
            onClick={handleImageClick}
            src={imgSrc}
            alt="Imagem do produto"
            loadingClass="skeleton"
          />
        </div>
        <div className="conteudo-container">
          <div className="texto-container">
            <div className="texto fonteRotulos">{produto.associacao}</div>
            <div className="texto referencia fonteSubtitulo">
              <span>
                {produto.referencia}
                {(() => {
                  // Prioriza faixa de preços (preco_min/preco_max)
                  if (produto.preco_min !== undefined || produto.preco_max !== undefined) {
                    const precoFormatado = formatPrecoRange(produto, moeda);
                    return precoFormatado ? <><br />{precoFormatado}</> : null;
                  }
                  // Fallback para preco único (legado)
                  if (produto.preco) {
                    return <><br />{`${Number(produto.preco.replace(",", ".")) > 0 ? `${moeda}${produto.preco}` : "A combinar"}`}</>;
                  }
                  return null;
                })()}
              </span>
            </div>
            <div className="texto texto-estoque fonteRotulos">
              {Number.parseInt(produto.estoque) >= 0 ? (
                <>Estoque geral: {produto.estoque}</>
              ) : (
                <>Sem controle de estoque</>
              )}
            </div>
          </div>
          {children && children}
        </div>
      </div>
    </ProdutoWrapper>
  );
};

export default Produto;
